import React from "react";

export const sumCounts = (obj) =>
  Object.values(obj || {}).reduce((a, b) => a + (Number(b) || 0), 0);

/**
 * One source's poll results: a labeled list of choices with count + percentage
 * bar. `total` is over ALL recorded votes (passed in). Votes cast for choices
 * that were later deleted have no current row, so they're collected into an
 * explicit "Removed options" bucket — otherwise the visible rows wouldn't sum to
 * 100% and the total wouldn't add up after a live poll is edited.
 *
 * Shared by the inline poll card editor (SwipeCardPollEditor) and the deck-level
 * Poll results summary (SwipeDeckPollResultsModal) so the two can't drift.
 */
export function PollResultsGroup({ title, counts, total, choices }) {
  const currentSum = (choices || []).reduce(
    (acc, _c, i) => acc + (Number(counts[String(i)]) || 0),
    0,
  );
  const removed = Math.max(0, total - currentSum);
  const removedPct = total ? Math.round((removed / total) * 100) : 0;
  return (
    <div className="mt-3 first:mt-0">
      <div className="text-[11px] font-semibold text-slate-500 mb-1.5">
        {title}
      </div>
      <div className="flex flex-col gap-1.5">
        {(choices || []).map((choice, i) => {
          const n = counts[String(i)] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          const label =
            `${choice.emoji ? `${choice.emoji} ` : ""}${
              choice.text || `Option ${i + 1}`
            }`;
          return (
            <div key={i}>
              <div className="flex items-center justify-between text-xs text-slate-700">
                <span className="truncate pr-2">{label}</span>
                <span className="tabular-nums text-slate-500 shrink-0">
                  {n} · {pct}%
                </span>
              </div>
              <div className="mt-0.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-violet-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
        {removed > 0 && (
          <div>
            <div className="flex items-center justify-between text-xs italic text-slate-500">
              <span className="truncate pr-2">
                Removed option{removed === 1 ? "" : "s"}
              </span>
              <span className="tabular-nums shrink-0">
                {removed} · {removedPct}%
              </span>
            </div>
            <div className="mt-0.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full bg-slate-400"
                style={{ width: `${removedPct}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
